import React, { useCallback, useState } from "react";

import {
  ActionButton,
  BottomActionSheet,
  CancelButton,
  HeaderInfo,
  HeaderSubtitle,
  HeaderTitle,
  SheetCard,
  SheetHeader,
} from "../../../../../components/BottomActionSheet";
import { ValuationBadge } from "../../../../../components/Valuation";
import { DropdownAction } from "../../../../../components/Dropdown/types";
import { INLINE_POSITION_INSIGHT_ID, inspectButtonIdOf } from "../../constants";
import { buildHoldingDisplay, priceOverrideEntryFor } from "./holdingDisplay";
import { selectOnBareClick } from "./inspectClick";
import { HoldingItemProps } from "./types";
import { KebabButton } from "../../../../../components/KebabButton";
import { ButtonTypes } from "../../../../../components/Button";
import {
  AllocationNote,
  CardInspectButton,
  CardInstrument,
  CardMeta,
  CardName,
  CardPrice,
  CardPriceMeta,
  CardPriceUpdatedNote,
  CardQuantity,
  CardReturn,
  CardRow,
  CardTopActions,
  CardTopRow,
  CardValuationNote,
  CardValue,
  CardValueRow,
  Instrument,
  InstrumentMark,
  PriceOverrideButton,
  ReturnAmount,
  ReturnPercent,
} from "./styles";

// The sheet's variants are a superset of the dropdown's, so the tone is mapped
// across rather than passed through: `destructive` is the only shared one.
const sheetVariantOf = (action: DropdownAction) =>
  action.variant === "destructive" ? "destructive" : undefined;

export const HoldingCard: React.FC<HoldingItemProps> = ({
  holding,
  actions,
  onSetPrice,
  isSelected,
  onToggleSelect,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const {
    valueText,
    badge,
    valuationMessage,
    priceText,
    priceUpdated,
    instrumentSubtitle,
    quantityText,
    costBasisText,
    allocationText,
    markText,
    markColor,
    returnAmountText,
    returnPercentText,
    returnTone,
  } = buildHoldingDisplay(holding);

  const visibleActions = actions.filter((action) => action.show);
  const hasActions = visibleActions.length > 0;

  const priceOverrideEntry = priceOverrideEntryFor(holding, onSetPrice);

  const openSheet = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const runAction = useCallback(
    (action: () => void) => {
      closeSheet();
      action();
    },
    [closeSheet],
  );

  return (
    <CardRow
      $selected={isSelected}
      onClick={selectOnBareClick(() => onToggleSelect(holding))}
    >
      <CardTopRow>
        <CardInspectButton
          type="button"
          id={inspectButtonIdOf(holding.id)}
          aria-expanded={isSelected}
          aria-controls={isSelected ? INLINE_POSITION_INSIGHT_ID : undefined}
          aria-label={`Inspect ${holding.instrument.symbol} position`}
          onClick={() => onToggleSelect(holding)}
        >
          <Instrument>
            <InstrumentMark $color={markColor} aria-hidden="true">
              {markText}
            </InstrumentMark>
            <CardName>{holding.instrument.symbol}</CardName>
          </Instrument>
        </CardInspectButton>
        <CardTopActions>
          <CardReturn>
            <ReturnAmount $tone={returnTone}>{returnAmountText}</ReturnAmount>
            {returnPercentText && (
              <ReturnPercent>{returnPercentText}</ReturnPercent>
            )}
          </CardReturn>
          {hasActions && (
            <KebabButton
              size="small"
              onClick={openSheet}
              aria-label="Holding options"
              aria-haspopup="dialog"
              data-testid={`holding-actions-mobile-${holding.id}`}
            />
          )}
        </CardTopActions>
      </CardTopRow>
      <CardInstrument>{instrumentSubtitle}</CardInstrument>
      <CardValueRow>
        <CardValue>{valueText}</CardValue>
        {badge && <ValuationBadge badge={badge} hasVisibleNote={true} />}
      </CardValueRow>
      {allocationText && <AllocationNote>{allocationText}</AllocationNote>}
      {valuationMessage && (
        <CardValuationNote>{valuationMessage}</CardValuationNote>
      )}
      {priceOverrideEntry && (
        <PriceOverrideButton
          type={ButtonTypes.link}
          size="small"
          onClick={priceOverrideEntry.open}
        >
          {priceOverrideEntry.label}
        </PriceOverrideButton>
      )}
      <CardMeta>
        <CardPriceMeta data-testid={`holding-price-${holding.id}`}>
          <CardPrice>Price {priceText}</CardPrice>
          <CardPriceUpdatedNote priceUpdated={priceUpdated} />
        </CardPriceMeta>
        <CardQuantity>Quantity {quantityText}</CardQuantity>
        <span data-testid={`holding-cost-${holding.id}`}>
          Cost {costBasisText}
        </span>
      </CardMeta>

      {hasActions && (
        <BottomActionSheet
          isOpen={isSheetOpen}
          onClose={closeSheet}
          ariaLabel="Holding actions"
        >
          <SheetCard>
            <SheetHeader>
              <HeaderInfo>
                <HeaderTitle>{holding.instrument.symbol}</HeaderTitle>
                <HeaderSubtitle>Quantity {quantityText}</HeaderSubtitle>
              </HeaderInfo>
            </SheetHeader>
            {visibleActions.map((action) => (
              <ActionButton
                key={action.id}
                type="button"
                $variant={sheetVariantOf(action)}
                onClick={() => runAction(action.onClick)}
              >
                {action.label}
              </ActionButton>
            ))}
          </SheetCard>
          <SheetCard>
            <CancelButton type="button" onClick={closeSheet}>
              Cancel
            </CancelButton>
          </SheetCard>
        </BottomActionSheet>
      )}
    </CardRow>
  );
};
